import type { CompanionEmotion } from "@nano/content";

/**
 * Emotion state machine for the in-level companion. Pure and clock-free
 * (time and randomness are passed in) so it is unit tested; the React view
 * only renders `emotion` and shows the requested speech line.
 *
 * Reactions are short "holds" that fall back to calm; a mistake streak
 * slides from sad to (gentle) crying; a quiet stretch makes the companion
 * sleepy. Speech is rare on purpose: a global cooldown plus a chance per
 * situation, so a line feels like a moment rather than a ticker.
 */

export type CompanionEvent =
  | { readonly type: "start" }
  | { readonly type: "place"; readonly combo: number; readonly binFull: boolean }
  | { readonly type: "mistake" }
  | { readonly type: "undo" }
  | { readonly type: "hint" }
  | { readonly type: "phase" }
  | { readonly type: "pick" }
  | { readonly type: "win"; readonly stars: 1 | 2 | 3 }
  | { readonly type: "poke" }
  | { readonly type: "tick" };

/** Situations with their own pool of lines (see i18n/companion.vi.ts). */
export type LinePool =
  | "start"
  | "combo"
  | "bin"
  | "phase"
  | "oops"
  | "oopsAgain"
  | "cry"
  | "comeback"
  | "undo"
  | "hint"
  | "sleepy"
  | "wake"
  | "win3"
  | "win"
  | "poke";

export interface SpeechRequest {
  readonly pool: LinePool;
  /** Increases with every request so the view can tell repeats apart. */
  readonly serial: number;
  readonly at: number;
}

export interface CompanionState {
  readonly emotion: CompanionEmotion;
  /** When a transient emotion falls back to `next`; null holds it. */
  readonly until: number | null;
  readonly next: CompanionEmotion;
  /** Mistakes since the last correct move. */
  readonly mistakes: number;
  readonly lastInput: number;
  readonly lastSpeech: number;
  readonly speech: SpeechRequest | null;
  /** The level is won: the companion keeps celebrating. */
  readonly done: boolean;
}

export interface MachineOptions {
  /** Scales every speech chance (a calmer house can talk less). */
  readonly chatter?: number;
}

export const HOLD_MS: Partial<Record<CompanionEmotion, number>> = {
  happy: 1500,
  cheer: 2000,
  thinking: 1800,
  surprised: 1200,
  sad: 1800,
  crying: 2600,
};
export const IDLE_SLEEP_MS = 22_000;
export const SPEECH_COOLDOWN_MS = 8_000;
export const POKE_COOLDOWN_MS = 1_500;
/** Combo counts that may earn a cheering line. */
export const COMBO_MILESTONES: ReadonlySet<number> = new Set([3, 5, 8, 12, 16, 20]);
/** A streak this long turns sadness into tears. */
export const CRY_AFTER = 3;

const CHANCE: Record<LinePool, number> = {
  start: 1,
  combo: 0.7,
  bin: 0.35,
  phase: 0.8,
  oops: 0.25,
  oopsAgain: 0.5,
  cry: 1,
  comeback: 0.7,
  undo: 0.15,
  hint: 0.6,
  sleepy: 1,
  wake: 0.5,
  win3: 1,
  win: 1,
  poke: 1,
};

/** Lines that matter enough to skip the cooldown. */
const URGENT: ReadonlySet<LinePool> = new Set(["start", "win3", "win", "cry", "comeback"]);

export function initialCompanion(now: number): CompanionState {
  return {
    emotion: "calm",
    until: null,
    next: "calm",
    mistakes: 0,
    lastInput: now,
    lastSpeech: Number.NEGATIVE_INFINITY,
    speech: null,
    done: false,
  };
}

function feel(state: CompanionState, emotion: CompanionEmotion, now: number, next: CompanionEmotion = "calm"): CompanionState {
  const hold = HOLD_MS[emotion];
  return { ...state, emotion, until: hold === undefined ? null : now + hold, next };
}

function say(state: CompanionState, pool: LinePool, now: number, random: () => number, options: MachineOptions): CompanionState {
  const urgent = URGENT.has(pool);
  const cooldown = pool === "poke" ? POKE_COOLDOWN_MS : SPEECH_COOLDOWN_MS;
  if (!urgent && now - state.lastSpeech < cooldown) return state;
  const chance = urgent || pool === "poke" ? 1 : CHANCE[pool] * (options.chatter ?? 1);
  if (random() >= chance) return state;
  const serial = (state.speech?.serial ?? 0) + 1;
  return { ...state, lastSpeech: now, speech: { pool, serial, at: now } };
}

/** Advances the companion by one event. Never mutates `state`. */
export function react(
  state: CompanionState,
  event: CompanionEvent,
  now: number,
  random: () => number = Math.random,
  options: MachineOptions = {},
): CompanionState {
  if (event.type === "tick") return tick(state, now, random, options);
  if (event.type === "start") return say(feel(initialCompanion(now), "happy", now), "start", now, random, options);
  if (event.type === "poke") {
    const poked = say(state, "poke", now, random, options);
    return state.done ? poked : feel({ ...poked, lastInput: now }, "happy", now);
  }
  if (state.done) return state;

  const wasAsleep = state.emotion === "sleepy";
  const awake: CompanionState = { ...state, lastInput: now };
  switch (event.type) {
    case "pick":
      return wasAsleep ? say(feel(awake, "surprised", now), "wake", now, random, options) : awake;
    case "place": {
      const recovering = state.mistakes >= 2;
      const base = feel({ ...awake, mistakes: 0 }, event.binFull || event.combo >= 4 ? "cheer" : "happy", now);
      if (recovering) return say(base, "comeback", now, random, options);
      if (COMBO_MILESTONES.has(event.combo)) return say(base, "combo", now, random, options);
      if (event.binFull) return say(base, "bin", now, random, options);
      return base;
    }
    case "mistake": {
      const mistakes = state.mistakes + 1;
      const crying = mistakes >= CRY_AFTER;
      const base = feel({ ...awake, mistakes }, crying ? "crying" : "sad", now, crying ? "sad" : "calm");
      if (mistakes === CRY_AFTER) return say(base, "cry", now, random, options);
      if (mistakes === 1) return say(base, "oops", now, random, options);
      return say(base, "oopsAgain", now, random, options);
    }
    case "undo":
      return say(feel(awake, "thinking", now), "undo", now, random, options);
    case "hint":
      return say(feel(awake, "thinking", now), "hint", now, random, options);
    case "phase":
      return say(feel(awake, "surprised", now), "phase", now, random, options);
    case "win": {
      const won: CompanionState = { ...awake, done: true, mistakes: 0, emotion: event.stars === 3 ? "cheer" : "happy", until: null };
      return say(won, event.stars === 3 ? "win3" : "win", now, random, options);
    }
  }
}

function tick(state: CompanionState, now: number, random: () => number, options: MachineOptions): CompanionState {
  if (state.done) return state;
  if (state.emotion !== "sleepy" && now - state.lastInput >= IDLE_SLEEP_MS) {
    return say({ ...state, emotion: "sleepy", until: null, next: "calm" }, "sleepy", now, random, options);
  }
  if (state.until !== null && now >= state.until) {
    // Tears dry into sadness first, then calm.
    return feel(state, state.next, now, "calm");
  }
  return state;
}
